import { type Editor } from 'slate';
import EditorState, { Namespace, AhaEditorKey } from '../util/EditorState';

class RenderingUtils {
  /**
   * Force the editor to rerender.
   *
   * Slate has gotten pretty smart about preventing unnecessary rerenders
   * but there are some cases in which we want the editor to rerender because
   * of an external change.
   *
   * @param editor - Editor instance.
   */
  static forceRerender(editor: Editor) {
    const ahaEditor = EditorState.get(
      editor,
      Namespace.AhaEditor,
      AhaEditorKey.Instance
    );

    ahaEditor?.controller?.actionReRenderEditor();
  }

  /**
   * Force decorations to be updated for the editor.
   *
   * Useful if the decorations are affected by something that is outside the
   * editor's state.
   *
   * @param editor - Editor instance.
   */
  static forceComputeDecorations(editor: Editor, asynchronous = false) {
    const ahaEditor = EditorState.get(
      editor,
      Namespace.AhaEditor,
      AhaEditorKey.Instance
    );
    if (!ahaEditor?.controller) return;
    if (ahaEditor.needsComputeDecorations) return;

    if (!asynchronous) {
      ahaEditor.controller.actionForceComputeDecorations();
    } else {
      // We need to do the compute decorations asynchronously to ensure that
      // it happens outside any `apply` for the editor, otherwise we risk
      // the render trying to read inconsistent editor state. We also only
      // want to do one recomputation even if this function is called many times
      // (e.g. when deleting many annotations).
      //
      // Doing this asynchronously during focus prevents focus from working. I
      // can't figure out why, so I had to add the asynchronous flag.
      ahaEditor.needsComputeDecorations = true;
      setTimeout(() => {
        ahaEditor.needsComputeDecorations = false;
        ahaEditor.controller.actionForceComputeDecorations();
      });
    }
  }
}

export default RenderingUtils;
